import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { Modal } from 'antd';
import { DownloadingState, useStore } from '~src/store';
import { appLog } from '~src/utils/log';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import {
  newTenderShareFolderDownload,
  getTenderShareFolderFilesURLs,
  loadSharedTenderSubfolders,
  loadSharedTenderAttachment,
  loadSharedTenderVersionedFile,
} from '~src/services/api/shares';
import type { DbTenderFolder, DbFile, DbAttachment } from '~src/types/db';

// --- Types ---
type MixedFile = DbFile & DbAttachment;

interface DownloadShareMultipleParams {
  folders: DbTenderFolder[];
  files: MixedFile[];
  allFolders: DbTenderFolder[];
  shareId: string;
  shareKey: string;
  currentFolderId?: string | number | null;
  rootFolderId?: string | number | null;
  onDownloadStarted?: () => void;
}

// --- ZipStream Class ---
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start(zipName: string) {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(zipName);
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  private getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      const msg = `${uniquePath} (0.00 MB) - ERROR: ${error?.message}`;
      this.errorLog.push(msg);
      useStore.getState().addError(msg);
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;
    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      if (this.fileMap.has(uniqueFolderPath)) return;

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));
      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      // Ignore creation errors
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;
      if (this.endTime !== 0) return;

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];
      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];
      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
      }

      await this.zipWriter.close();
      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      setTimeout(
        () => useStore.getState().changeDownloadingActive(false),
        1500
      );
    } catch (error) {
      this.isAborted = true;
    }
  }
}

// --- Workers ---
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// --- Helpers ---
const uniqFoldersById = (folders: DbTenderFolder[]) => {
  const map = new Map<string, DbTenderFolder>();
  for (const f of folders) map.set(String(f.id), f);
  return Array.from(map.values());
};

// The API endpoint GET /tender_shares/{share_id}/tender_folders/{root_folder_id}/folders
// returns ALL descendant subfolders of a root folder in one flat call (parent_id allows
// local tree building). root_folder_id MUST be a true root-level folder id – calling
// it with a non-root id returns 400 from TenderRootFolderCheckMiddleware.
// Therefore: one call per root, no recursion needed.
const loadAllSharedSubfolders = async ({
  shareId,
  shareKey,
  rootFolderIds,
}: {
  shareId: string;
  shareKey: string;
  rootFolderIds: number[];
}): Promise<DbTenderFolder[]> => {
  const result: DbTenderFolder[] = [];

  for (const rootId of rootFolderIds) {
    try {
      const children = await loadSharedTenderSubfolders(
        ['shared-subfolders', shareId, rootId],
        { share_id: shareId, share_key: shareKey, root_folder_id: rootId }
      );
      if (children?.length) result.push(...children);
    } catch (e) {
      // continue to next root
    }
  }

  return uniqFoldersById(result);
};

const ModalConfirmDownload = async () => {
  return new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: 'Large download',
      content:
        'Selected content is large (>500 files). Downloading may take time. Continue?',
      okText: 'Download',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

// --- Main Function ---
export const downloadShareTenderMultiple = async ({
  folders,
  files,
  allFolders,
  shareId,
  shareKey,
  currentFolderId,
  rootFolderId,
  onDownloadStarted,
}: DownloadShareMultipleParams): Promise<void> => {
  try {
    const fileQueue: any[] = [];
    let totalFiles = 0;
    let totalSize = 0;
    const folderPathsToCreate: string[] = [];

    // -------------------------------------------------------------
    // PREPARE: Global Context Tree
    // -------------------------------------------------------------
    // NOTE: We deliberately DO NOT call GET /tender_shares/{share_id}/tender_folders
    // to list root folders. That endpoint is currently gated server-side by
    // TenderRootFolderCheckMiddleware and returns 400 ("Invalid or missing
    // root_folder_id parameter"), even though per Swagger it requires no such
    // parameter. We instead derive root folders from the locally-known tree
    // (allFolders + the user-selected `folders` + an explicit `rootFolderId`
    // when provided via props/URL).
    const localTree = uniqFoldersById([...(allFolders || []), ...folders]);

    // Walk up the parent chain locally to find the topmost ancestor.
    const walkUpToLocalTop = (
      id: string | number
    ): DbTenderFolder | undefined => {
      let current = localTree.find((f) => String(f.id) === String(id));
      while (current && current.parent_id != null) {
        const parent = localTree.find(
          (f) => String(f.id) === String(current!.parent_id)
        );
        if (!parent) break;
        current = parent;
      }
      return current;
    };

    let sharedRootFolders: DbTenderFolder[] = [];
    if (rootFolderId) {
      const top = walkUpToLocalTop(rootFolderId);
      if (top) sharedRootFolders = [top];
    }
    if (!sharedRootFolders.length) {
      const idSet = new Set(localTree.map((f) => String(f.id)));
      sharedRootFolders = localTree.filter(
        (f) => !f.parent_id || !idSet.has(String(f.parent_id))
      );
    }

    // The API's TenderRootFolderCheckMiddleware rejects any id whose parent_id
    // is not null. Only call the endpoint for ids we can prove are roots; for
    // the rest we use the locally-known tree as the descendants source.
    const provableRootIds = sharedRootFolders
      .filter((f) => f.parent_id == null)
      .map((f) => Number(f.id));
    const allDescendants = provableRootIds.length
      ? await loadAllSharedSubfolders({
          shareId,
          shareKey,
          rootFolderIds: provableRootIds,
        })
      : allFolders || [];

    const fullContextTree = uniqFoldersById([
      ...(allFolders || []),
      ...(sharedRootFolders || []),
      ...(allDescendants || []),
      ...folders,
    ]);

    // -------------------------------------------------------------
    // 1. Process Selected Folders
    // -------------------------------------------------------------
    for (const folder of folders) {
      const rootFolderName = folder.name;

      const structure = buildFolderWithPathStructure(folder, fullContextTree);

      // Determine "Base Path" to strip (e.g. "Root/Parent/Selected" -> "Selected")
      const rootFolderEntry = structure.find(
        (s: any) => String(s.id) === String(folder.id)
      );

      let parentPathPrefix = '';
      if (rootFolderEntry && rootFolderEntry.path) {
        const parts = rootFolderEntry.path.split('/');
        if (parts.length > 1) {
          parts.pop();
          parentPathPrefix = parts.join('/') + '/';
        }
      }

      // Collect Empty Folders
      const currentFolderPaths = uniqFoldersById(
        structure.map((s: any) => ({ id: s.id, name: s.path } as any))
      )
        .map((x: any) => x.name)
        .filter(Boolean)
        .map((p: string) => {
          if (parentPathPrefix && p.startsWith(parentPathPrefix)) {
            return p.substring(parentPathPrefix.length);
          }
          return p;
        });

      const adjustedRootName = rootFolderName;
      const allPathsSet = new Set<string>(currentFolderPaths);
      if (!allPathsSet.has(adjustedRootName)) {
        currentFolderPaths.unshift(adjustedRootName);
      }
      folderPathsToCreate.push(...currentFolderPaths);

      // Fetch Files.
      // For root folders folder.parent_id is null, so rootFolderId = folder.id
      // (both path segments become the same value, which is the intended behaviour
      // for "all files in this root folder recursively").
      const effectiveRootFolderId = folder.parent_id || folder.id;
      const folderFiles = await newTenderShareFolderDownload('files', {
        shareId,
        shareKey,
        rootFolderId: effectiveRootFolderId,
        folderId: folder.id,
      });

      totalFiles += folderFiles.length;
      totalSize += folderFiles.reduce(
        (acc: number, f: any) => acc + (f.file_size || 0),
        0
      );

      // Add Files to Queue
      folderFiles.forEach((file: any) => {
        const fileFolderId = file.folder_id;

        const rawFolderPath =
          structure.find((f: any) => {
            if (fileFolderId == null) return false;
            return (
              String(f.id) === String(fileFolderId) ||
              (f.using_key != null &&
                String(f.using_key) === String(fileFolderId))
            );
          })?.path || '';

        let safeFolderPath = rawFolderPath;
        if (parentPathPrefix && rawFolderPath.startsWith(parentPathPrefix)) {
          safeFolderPath = rawFolderPath.substring(parentPathPrefix.length);
        }

        if (!safeFolderPath) safeFolderPath = rootFolderName;

        fileQueue.push({
          type: 'folder_file',
          file: file,
          fullPath: safeFolderPath
            ? `${safeFolderPath}/${file.file_name}`
            : file.file_name,
          // Carry root folder id so batch_download_urls can satisfy the middleware
          rootFolderId: effectiveRootFolderId,
        });
      });
    }

    // -------------------------------------------------------------
    // 2. Process Selected Individual Files
    // -------------------------------------------------------------
    for (const file of files) {
      const size = (file as any).size || (file as any).file_size || 0;
      totalFiles += 1;
      totalSize += size;

      // FIX: Robust name fallback to avoid "undefined"
      const fileName =
        file.name || file.title || (file as any).file_name || `file-${file.id}`;

      let calculatedPath = fileName;

      const fileFolderId = file.folder_id;
      if (fileFolderId) {
        // Find the folder in our global tree
        const parentFolder = fullContextTree.find(
          (f) => String(f.id) === String(fileFolderId)
        );

        if (parentFolder) {
          // Build absolute path from tree root
          const struct = buildFolderWithPathStructure(
            parentFolder,
            fullContextTree
          );
          const folderEntry = struct.find(
            (s) => String(s.id) === String(fileFolderId)
          );

          if (folderEntry && folderEntry.path) {
            let fullPath = folderEntry.path;

            // FIX: Strip the Share Root prefix logic
            // If the file's path starts with a share root's name (e.g. "B04 Leverandør"),
            // we strip that root folder so the file sits at the ZIP root (or relative structure).
            for (const root of sharedRootFolders) {
              const rootPrefix = root.name + '/';
              if (fullPath.startsWith(rootPrefix)) {
                fullPath = fullPath.substring(rootPrefix.length);
                break; // Stop after stripping the root
              } else if (fullPath === root.name) {
                // If the file is directly inside the root folder, stripping it leaves empty string
                fullPath = '';
                break;
              }
            }

            // Only prepend path if it's not empty
            calculatedPath = fullPath ? `${fullPath}/${fileName}` : fileName;

            // Ensure folder creation
            if (fullPath) folderPathsToCreate.push(fullPath);
          }
        }
      }

      fileQueue.push({
        type: 'individual_file',
        file: file,
        calculatedPath: calculatedPath, // Pass the fixed, stripped path
      });
    }

    // -------------------------------------------------------------
    // 3. Start Download
    // -------------------------------------------------------------
    if (totalFiles > 500) {
      const confirm = await ModalConfirmDownload();
      if (!confirm) return;
    }

    if (onDownloadStarted) onDownloadStarted();

    const zipStream = new ZipStream(totalFiles, totalSize);
    useStore.getState().totalDownloadFiles(totalFiles);
    zipStream.start(`C-WEB-Share-${Date.now()}.zip`);

    if (zipStream.isAborted) return;

    // Create empty folders first
    const uniquePaths = [...new Set(folderPathsToCreate)];
    for (const p of uniquePaths) {
      await zipStream.addEmptyFolder(p);
    }

    if (fileQueue.length === 0) {
      await zipStream.finish();
      return;
    }

    // -------------------------------------------------------------
    // 4. Worker Processing
    // -------------------------------------------------------------
    const NUM_WORKERS = 5;
    const workers: Worker[] = [];
    let activeWorkers = 0;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const getFileUrlAndPath = async (item: any) => {
      let url = '';
      let fullPath = '';

      if (item.type === 'folder_file') {
        fullPath = item.fullPath;
        const file = item.file;
        // Pass the folder's root id so TenderRootFolderCheckMiddleware is satisfied.
        // The response schema VFDownloadUrlUsingKeyVM has { key, url } – match on 'key'.
        const urls = await getTenderShareFolderFilesURLs(
          shareId,
          shareKey,
          [file.using_key],
          item.rootFolderId
        );
        const urlData =
          (urls || []).find(
            (u: any) => String(u?.key) === String(file.using_key)
          ) || (urls || [])[0];
        url = (urlData as any)?.url;
      } else {
        // --- Case B: Individually selected file ---
        const file = item.file;
        const fileName =
          file.name ||
          file.title ||
          (file as any).file_name ||
          `file-${file.id}`;

        // Use the pre-calculated path
        if (item.calculatedPath) {
          fullPath = item.calculatedPath;
        } else {
          fullPath = fileName;
        }

        const isFolderFile =
          file.folder_id != null && Number(file.folder_id) > 0;

        try {
          if (!isFolderFile) {
            const res = await loadSharedTenderAttachment({
              file_guid: String(file.id),
              share_id: shareId,
              share_key: shareKey,
            });
            url = res?.location || '';
          } else {
            const fileDetails = file.files?.[file.files.length - 1];
            if (fileDetails) {
              const res = await loadSharedTenderVersionedFile({
                file_guid: String(fileDetails.file_id),
                share_id: shareId,
                share_key: shareKey,
                folder_id: file.folder_id,
                root_folder_id: rootFolderId || file.folder_id,
                versioned_file_id: String(file.id),
              });
              url = res?.location || '';
            }
          }
        } catch (e) {
          appLog('Error fetching individual file url', e);
        }
      }
      return { url, fullPath };
    };

    const finishIfDone = async () => {
      if (fileQueue.length === 0 && activeWorkers === 0) {
        setTimeout(async () => {
          if (fileQueue.length === 0 && activeWorkers === 0) {
            await zipStream.finish();
            workers.forEach((w) => w.terminate());
          }
        }, 300);
      }
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) return;

      const nextItem = fileQueue.shift();
      if (!nextItem) {
        await finishIfDone();
        return;
      }
      activeWorkers++;

      const processed = await getFileUrlAndPath(nextItem);

      if (!processed.url) {
        const err = `Missing URL for ${processed.fullPath}`;
        zipStream.errorLog.push(err);
        useStore.getState().addError(err);
        activeWorkers--;
        // Try next file
        if (fileQueue.length > 0) {
          await assignFileToWorker(worker);
        } else {
          await finishIfDone();
        }
        return;
      }

      worker.postMessage({
        file: { url: processed.url, file_name: processed.fullPath },
      });
    };

    // Attach listeners
    for (const worker of workers) {
      worker.onmessage = async (event) => {
        const { success, file, blob, error } = event.data;
        try {
          if (success) {
            await zipStream.addFile(file.file_name, blob);
          } else {
            const msg = `${file.file_name} - ERROR: ${error}`;
            zipStream.errorLog.push(msg);
            useStore.getState().addError(msg);
          }
        } finally {
          activeWorkers--;
          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else {
            await finishIfDone();
          }
        }
      };

      worker.onerror = async (e) => {
        const msg = `Worker Error: ${e.message}`;
        zipStream.errorLog.push(msg);
        useStore.getState().addError(msg);
        activeWorkers--;
        await finishIfDone();
      };

      await assignFileToWorker(worker);
    }
  } catch (err) {
    appLog('downloadShareTenderMultiple -> global error', err);
    useStore.getState().changeDownloadingActive(false);
  }
};
